import { useEffect, useState } from 'react';
import { Check, ArrowRight, Building2, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';

const gbp = (pence) => `£${(pence / 100).toLocaleString('en-GB')}`;

export const CouncilPlans = ({ onSubscribe, loading }) => {
  const [cat, setCat] = useState(null);
  const [la, setLa] = useState('stockton');
  const [annual, setAnnual] = useState(false);

  useEffect(() => { api.get('/billing/tvca-catalogue').then(({ data }) => setCat(data)); }, []);

  if (!cat) return <div className="h-40 flex items-center justify-center"><div className="w-6 h-6 rounded bg-brand-orange animate-pulse" /></div>;

  const plans = cat.plans.filter(p => p.la_slug === la);
  const cadence = annual ? 'annual' : 'monthly';

  return (
    <div data-testid="council-plans">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-lg font-extrabold text-ink mb-1">Per-district council licences</h2>
          <p className="font-body text-sm text-ink/60 max-w-xl">Full BDAF intelligence for one local authority. Priced per district, billed to the council. All prices exclude VAT — Stripe adds UK VAT ({cat.vat.rate_pct}%) at checkout.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`font-body text-sm ${!annual ? 'text-ink' : 'text-ink/40'}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="council-billing-toggle" />
          <span className={`font-body text-sm ${annual ? 'text-ink' : 'text-ink/40'}`}>Annual</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="council-la-picker">
        {Object.entries(cat.la_names).map(([slug, name]) => (
          <button key={slug} type="button" onClick={() => setLa(slug)} data-testid={`council-la-${slug}`}
            className={`px-4 py-2 rounded-full font-body text-xs border transition-colors ${la === slug ? 'bg-ink text-white border-ink' : 'bg-white text-ink/70 border-ink/15 hover:border-ink/40'}`}>
            <Building2 className="w-3 h-3 inline mr-1.5 -mt-0.5" />{name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
        {plans.map((p) => {
          const highlight = p.tier === 'premium';
          const amount = annual ? p.annual_amount : p.monthly_amount;
          const configured = annual ? p.annual_configured : p.monthly_configured;
          return (
            <div key={p.tier} data-testid={`council-plan-${p.tier}`} className={`rounded-[6px] p-6 flex flex-col ${highlight ? 'bg-ink text-white border border-ink shadow-xl' : 'bg-white border border-ink/10'}`}>
              {highlight && <Badge className="self-start bg-brand-orange text-white border-0 font-body text-[10px] mb-3">Recommended for LA teams</Badge>}
              <h3 className={`font-display text-2xl font-extrabold mb-1 ${highlight ? 'text-white' : 'text-ink'}`}>{p.tier_name}</h3>
              <p className={`font-body text-xs mb-5 ${highlight ? 'text-white/60' : 'text-ink/50'}`}>{p.la_name} district licence</p>
              <div className="mb-5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`font-display text-4xl font-extrabold leading-none ${highlight ? 'text-white' : 'text-ink'}`} data-testid={`council-price-${p.tier}`}>{gbp(amount)}</span>
                  <span className={`font-body text-xs ${highlight ? 'text-white/60' : 'text-ink/50'}`}>{annual ? '/ year' : '/ month'} + VAT</span>
                </div>
                <p className={`font-body text-[11px] mt-1.5 ${highlight ? 'text-white/40' : 'text-ink/40'}`}>
                  {annual ? `${gbp(p.monthly_amount)}/mo equivalent · 14-day free trial` : `${gbp(p.annual_amount)} billed annually · 14-day free trial`}
                </p>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 font-body text-xs ${highlight ? 'text-white/80' : 'text-ink/70'}`}>
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-orange" strokeWidth={3} /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => onSubscribe(annual ? p.annual_lookup : p.monthly_lookup)} disabled={loading || !configured}
                data-testid={`council-plan-${p.tier}-cta`}
                className={`w-full font-body text-sm ${highlight ? 'bg-brand-orange hover:bg-brand-orange/90 text-white' : 'bg-ink hover:bg-ink/90 text-white'}`}>
                {configured ? `Subscribe · ${p.la_name}` : 'Coming soon'} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-start gap-3 rounded-[6px] border border-ink/10 bg-white p-4 max-w-4xl" data-testid="council-vat-note">
        <ShieldCheck className="w-4 h-4 text-forest mt-0.5 flex-shrink-0" />
        <p className="font-body text-[12px] text-ink/60 leading-relaxed">
          Invoiced by <strong className="text-ink">{cat.seller.legal_name}</strong> trading as Adoptic · Company No. {cat.seller.company_number} · VAT Reg. {cat.vat.number}.
          {' '}{cat.vat.note} Card, Direct Debit and BACS invoice (30-day terms) accepted.
        </p>
      </div>
    </div>
  );
};
